import { Group, Object3D, MeshStandardMaterial, Mesh } from 'three';
import { UserDataObjectType, UserDataProp } from '../types';

export abstract class RenderUtils {
	static clearGroup(group: Group) {
		group.traverse((object) => {
			if (RenderUtils.isMesh(object)) {
				if (object.geometry) object.geometry.dispose();

				if (object.material) {
					if (Array.isArray(object.material)) {
						object.material.forEach((material) => material.dispose());
					} else {
						object.material.dispose();
					}
				}
			}
		});

		group.clear();
	}

	static disposeObject(object: Object3D) {
		if (RenderUtils.isMesh(object)) {
			if (object.geometry) object.geometry.dispose();
			if (object.material) {
				if (Array.isArray(object.material)) {
					object.material.forEach((material) => material.dispose());
				} else {
					object.material.dispose();
				}
			}
		}

		object.traverse((child) => {
			if (RenderUtils.isMesh(child)) {
				if (child.geometry) child.geometry.dispose();
				if (child.material) {
					if (Array.isArray(child.material)) {
						child.material.forEach((material) => material.dispose());
					} else {
						child.material.dispose();
					}
				}
			}
		});
	}

	static disposeMaterial(object: Object3D) {
		if (!RenderUtils.isMesh(object)) return;

		if (Array.isArray(object.material)) {
			for (const material of object.material) {
				const mat = material as MeshStandardMaterial;

				mat.dispose();
				mat.map?.dispose();
				mat.normalMap?.dispose();
				mat.roughnessMap?.dispose();
				mat.metalnessMap?.dispose();
			}
		} else {
			const mat = object.material as MeshStandardMaterial;

			mat.dispose();
			mat.map?.dispose();
			mat.normalMap?.dispose();
			mat.roughnessMap?.dispose();
			mat.metalnessMap?.dispose();
		}
	}

	static isMesh(object: Object3D): object is Mesh {
		return object instanceof Mesh;
	}

	static getModelParts(model: Object3D): {
		handle: Mesh | null;
		originPoint: Mesh | null;
		upperPost: Mesh | null;
		lowerPost: Mesh | null;
		middlePost: Mesh | null;
		upperEnding: Mesh | null;
		lowerEnding: Mesh | null;
		upperEndingAdjustable: Mesh | null;
		lowerEndingAdjustable: Mesh | null;
		objectsAvailableSpaceObjects: Object3D[];
		measurementPoints: Object3D[];
	} {
		let originPoint: Mesh | null = null,
			upperPost: Mesh | null = null,
			lowerPost: Mesh | null = null,
			handle: Mesh | null = null,
			middlePost: Mesh | null = null,
			upperEnding: Mesh | null = null,
			lowerEnding: Mesh | null = null,
			upperEndingAdjustable: Mesh | null = null,
			lowerEndingAdjustable: Mesh | null = null;

		const objectsAvailableSpaceObjects: Object3D[] = [];
		const measurementPoints: Object3D[] = [];

		model.traverse((object) => {
			if (object.userData[UserDataProp.OBJECTS_AVAILABLE_SPACE])
				objectsAvailableSpaceObjects.push(object);

			switch (object.userData[UserDataProp.OBJECT_TYPE]) {
				case UserDataObjectType.ORIGIN_POINT:
					originPoint = object as Mesh;
					break;
				case UserDataObjectType.HANDLE:
					handle = object as Mesh;
					break;
				case UserDataObjectType.UPPER_POST:
					upperPost = object as Mesh;
					break;
				case UserDataObjectType.LOWER_POST:
					lowerPost = object as Mesh;
					break;
				case UserDataObjectType.MIDDLE_POST:
					middlePost = object as Mesh;
					break;
				case UserDataObjectType.UPPER_ENDING:
					upperEnding = object as Mesh;
					break;
				case UserDataObjectType.LOWER_ENDING:
					lowerEnding = object as Mesh;
					break;
				case UserDataObjectType.UPPER_ENDING_ADJUSTABLE:
					upperEndingAdjustable = object as Mesh;
					break;
				case UserDataObjectType.LOWER_ENDING_ADJUSTABLE:
					lowerEndingAdjustable = object as Mesh;
					break;
				case UserDataObjectType.MEASUREMENT_POINT:
					measurementPoints.push(object);
					break;

				default:
					break;
			}
		});

		return {
			handle,
			originPoint,
			upperPost,
			lowerPost,
			middlePost,
			upperEnding,
			lowerEnding,
			upperEndingAdjustable,
			lowerEndingAdjustable,
			objectsAvailableSpaceObjects,
			measurementPoints,
		};
	}
}
